import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';

function MissionScreen() {
  const { dispatch } = useApp();
  const [missionQuery, setMissionQuery] = useState('');
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'system',
      content: '👋 Welcome to AI Mission Creation! Describe the mission you want to create and who should be assigned to it.',
      timestamp: new Date().toLocaleTimeString(),
      isWelcome: true
    }
  ]);
  const chatEndRef = useRef(null);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!missionQuery.trim()) return;

    setIsSubmitting(true);
    
    // Add user message to chat
    const userMessage = {
      type: 'user',
      content: missionQuery,
      timestamp: new Date().toLocaleTimeString()
    };
    setChatHistory(prev => [...prev, userMessage]);

    // Add loading message
    const loadingMessage = {
      type: 'system',
      content: 'Creating mission...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    setChatHistory(prev => [...prev, loadingMessage]);

    setStatus(
      <div className="flex items-center justify-center gap-2 text-yellow-400">
        <div className="spinner w-5 h-5"></div>
        Processing mission request...
      </div>
    );

    console.log('=== MISSION AI DIRECT ASSIGN REQUEST ===');
    console.log('Query:', missionQuery);
    console.log('Timestamp:', new Date().toISOString());

    try {
      const requestBody = {
        query: missionQuery
      };

      console.log('Request body:', requestBody);
      console.log('Endpoint:', 'https://langraphdev-development.up.railway.app/api/hey-mission/direct-assign');

      const response = await fetch('https://langraphdev-development.up.railway.app/api/hey-mission/direct-assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers));

      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('=== MISSION AI RESPONSE (JSON) ===');
        console.log('Parsed response:', responseData);
      } catch (parseError) {
        console.log('=== MISSION AI RESPONSE (NOT JSON) ===');
        console.log('Failed to parse as JSON:', parseError.message);
        
        responseData = { 
          error: 'Invalid response format', 
          rawResponse: responseText.substring(0, 500) + '...' 
        };
      }

      // Remove loading message
      setChatHistory(prev => prev.filter(msg => !msg.isLoading));

      if (response.ok && responseData && !responseData.error) {
        // Success response
        const successMessage = {
          type: 'assistant',
          content: responseData.message || responseData.result || 'Mission created successfully!',
          timestamp: new Date().toLocaleTimeString(),
          data: responseData,
          isSuccess: true
        };
        setChatHistory(prev => [...prev, successMessage]);

        setStatus(
          <div className="text-green-400">
            ✅ Mission created successfully!
          </div>
        );
      } else {
        // Error response
        const errorMsg = responseData?.error || responseData?.message || `HTTP ${response.status}: ${response.statusText}`;
        const errorMessage = {
          type: 'error',
          content: `❌ Failed to create mission: ${errorMsg}`,
          timestamp: new Date().toLocaleTimeString()
        };
        setChatHistory(prev => [...prev, errorMessage]);

        setStatus(
          <div className="text-red-400">
            ❌ Failed to create mission: {errorMsg}
          </div>
        );
      }

    } catch (error) {
      console.error('Mission creation error:', error);
      
      // Remove loading message
      setChatHistory(prev => prev.filter(msg => !msg.isLoading));

      const errorMessage = {
        type: 'error',
        content: `❌ Network error: ${error.message}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setChatHistory(prev => [...prev, errorMessage]);

      setStatus(
        <div className="text-red-400">
          ❌ Network error: {error.message}
        </div>
      );
    }

    console.log('=== END MISSION AI DEBUG ===');
    setIsSubmitting(false);
    setMissionQuery(''); // Clear input after sending
  };

  const goBackToDashboard = () => {
    dispatch({ type: ActionTypes.SET_SCREEN, payload: 'dashboard' });
  };

  const clearChat = () => {
    setChatHistory([
      {
        type: 'system',
        content: '👋 Chat cleared! Describe the mission you want to create and who should be assigned to it.',
        timestamp: new Date().toLocaleTimeString(),
        isWelcome: true
      }
    ]);
    setStatus(null);
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-auto h-[85vh] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white">AI Mission Creator</h1>
          <p className="text-gray-400 text-sm">Chat with AI to create and assign missions naturally</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={clearChat}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg text-sm border border-gray-600"
          >
            Clear Chat
          </button>
          <button 
            onClick={goBackToDashboard}
            className="text-sm text-gray-400 hover:text-white"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-gray-900/50 rounded-lg p-4 overflow-y-auto mb-4 space-y-3">
        {chatHistory.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${
              message.type === 'user' 
                ? 'bg-gray-700 text-white border border-gray-600' 
                : message.type === 'error'
                ? 'bg-red-900/50 text-red-200 border border-red-500'
                : message.isSuccess
                ? 'bg-gray-600 text-white border border-gray-500'
                : message.isWelcome
                ? 'bg-black text-gray-200 border border-gray-600'
                : 'bg-gray-700 text-gray-200 border border-gray-600'
            }`}>
              <div className="text-sm">
                {message.isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4"></div>
                    {message.content}
                  </div>
                ) : (
                  message.content
                )}
              </div>
              <div className="text-xs opacity-70 mt-1">
                {message.timestamp}
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={missionQuery}
          onChange={(e) => setMissionQuery(e.target.value)}
          placeholder="e.g., Create mission m_ui_redesign for redesigning the main menu interface and assign it to Arya"
          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-3 text-sm"
          disabled={isSubmitting}
        />
        <button
          type="submit"
          disabled={isSubmitting || !missionQuery.trim()}
          className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 flex items-center gap-2 border border-gray-600"
        >
          {isSubmitting ? (
            <>
              <div className="spinner w-4 h-4"></div>
              Send
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Send
            </>
          )}
        </button>
      </form>

      {/* Status Bar */}
      {status && (
        <div className="mt-3 text-center text-sm">
          {status}
        </div>
      )}

      {/* Example Prompts */}
      <div className="mt-3 text-xs text-gray-500">
        <p className="mb-1">💡 <strong>Example prompts:</strong></p>
        <div className="space-y-1 text-gray-400">
          <p>• "Create mission m_combat_system for implementing player combat mechanics and assign it to Arya"</p>
          <p>• "Make a new mission for UI improvements and give it to the best designer"</p>
          <p>• "Create mission m_bug_fixes for fixing critical gameplay bugs, deadline next Friday, assign to Arya"</p>
        </div>
      </div>
    </div>
  );
}

export default MissionScreen;